/**
 * Noara Cart Drawer
 * AJAX-powered slide-in cart with quantity controls, remove, and live updates.
 * @module noara-cart
 */
(function () {
  'use strict';

  /* ── DOM refs ─────────────────────────────────────────────────────── */
  const drawer    = document.querySelector('[data-cart-drawer]');
  const overlay   = document.querySelector('[data-cart-overlay]');
  const itemsList = document.querySelector('[data-cart-items]');
  const emptyEl   = document.querySelector('[data-cart-empty]');
  const footerEl  = document.querySelector('[data-cart-footer]');
  const subtotalEl = document.querySelector('[data-cart-subtotal]');
  const statusEl  = document.querySelector('[data-cart-status]');
  const errorEl   = document.querySelector('[data-cart-error]');
  const closeBtns = document.querySelectorAll('[data-cart-close]');

  if (!drawer || !overlay) return;

  /** @type {boolean} Guard to prevent overlapping cart mutations */
  let busy = false;

  /** @type {HTMLElement|null} Element that opened the drawer (for focus return) */
  let triggerEl = null;

  /* ── Helpers ──────────────────────────────────────────────────────── */

  /**
   * Format a Shopify price (in cents) using the shop's money format.
   * @param {number} cents - Price in cents.
   * @returns {string} Formatted price string.
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    /* Use the Shopify money_format if available, otherwise fallback */
    const format = window.Shopify && window.Shopify.money_format
      ? window.Shopify.money_format
      : '€{{amount}}';
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString());
  }

  /**
   * Perform a fetch request to the Shopify AJAX Cart API.
   * Cancels any pending request via AbortController.
   * @param {string} url - API endpoint.
   * @param {Object} [options] - Fetch options.
   * @returns {Promise<Object>} Parsed JSON response.
   */
  async function cartFetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || response.statusText);
    }

    return response.json();
  }

  /**
   * Show an error message in the cart drawer.
   * @param {string} message - Error text.
   */
  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }, 4000);
  }

  /**
   * Update all cart-count badges across the page.
   * @param {number} count - Total items in cart.
   */
  function updateCartCount(count) {
    /* data-cart-count badges (snippet) */
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count.toString();
      if (count > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
    /* .cart-count badges (header) */
    document.querySelectorAll('.cart-count').forEach((badge) => {
      badge.textContent = count.toString();
      badge.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ── Render ──────────────────────────────────────────────────────── */

  /**
   * Render the cart drawer contents from a cart object.
   * @param {Object} cart - Shopify cart JSON object.
   */
  function renderCart(cart) {
    /* Clear existing items */
    itemsList.textContent = '';

    /* Always update badges first */
    updateCartCount(cart.item_count);

    if (cart.item_count === 0) {
      emptyEl.style.display = '';
      footerEl.style.display = 'none';
      itemsList.style.display = 'none';
      statusEl.textContent = emptyEl.querySelector('.cart-drawer__empty-text').textContent;
      return;
    }

    emptyEl.style.display = 'none';
    footerEl.style.display = '';
    itemsList.style.display = '';

    cart.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'cart-drawer__item';
      li.setAttribute('data-cart-item-key', item.key);

      let imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url
        : (item.image || '');
      if (imgSrc) {
        imgSrc += (imgSrc.indexOf('?') > -1 ? '&' : '?') + 'width=160';
      }

      const variantTitle = item.variant_title || '';

      li.innerHTML = [
        imgSrc
          ? '<img class="cart-drawer__item-image" src="' + imgSrc + '" alt="' + item.product_title.replace(/"/g, '&quot;') + '" width="80" height="80" loading="lazy">'
          : '<div class="cart-drawer__item-image" aria-hidden="true"></div>',
        '<div class="cart-drawer__item-details">',
          '<p class="cart-drawer__item-title">',
            '<a href="' + item.url + '">' + item.product_title + '</a>',
          '</p>',
          variantTitle ? '<p class="cart-drawer__item-variant">' + variantTitle + '</p>' : '',
          '<p class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>',
          '<div class="cart-drawer__qty">',
            '<button class="cart-drawer__qty-btn" data-qty-minus data-key="' + item.key + '" aria-label="' + (drawer.dataset.labelDecrease || '') + '">',
              '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><line x1="2" y1="6" x2="10" y2="6"/></svg>',
            '</button>',
            '<span class="cart-drawer__qty-value" aria-label="' + (drawer.dataset.labelQuantity || '') + '">' + item.quantity + '</span>',
            '<button class="cart-drawer__qty-btn" data-qty-plus data-key="' + item.key + '" aria-label="' + (drawer.dataset.labelIncrease || '') + '">',
              '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><line x1="6" y1="2" x2="6" y2="10"/><line x1="2" y1="6" x2="10" y2="6"/></svg>',
            '</button>',
          '</div>',
        '</div>'
      ].join('');

      itemsList.appendChild(li);
    });

    subtotalEl.textContent = formatMoney(cart.total_price);
    statusEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' itens');
  }

  /* ── API actions ─────────────────────────────────────────────────── */

  /**
   * Fetch the current cart and re-render.
   * @returns {Promise<void>}
   */
  async function refreshCart() {
    try {
      const cart = await cartFetch('/cart.js');
      renderCart(cart);
    } catch (err) {
      showError(err.message);
    }
  }

  /**
   * Update a cart item quantity.
   * @param {string} key - Line item key.
   * @param {number} quantity - New quantity (0 to remove).
   * @returns {Promise<void>}
   */
  async function updateItem(key, quantity) {
    if (busy) return;
    busy = true;
    try {
      const cart = await cartFetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      renderCart(cart);
    } catch (err) {
      showError(err.message);
    } finally {
      busy = false;
    }
  }

  /**
   * Remove a cart item.
   * @param {string} key - Line item key.
   * @returns {Promise<void>}
   */
  async function removeItem(key) {
    await updateItem(key, 0);
  }

  /**
   * Add a product variant to the cart and open the drawer.
   * @param {number} variantId - Variant ID to add.
   * @param {number} [quantity=1] - Quantity to add.
   * @param {Object} [properties] - Line item properties.
   * @returns {Promise<void>}
   */
  async function addItem(variantId, quantity, properties) {
    const body = {
      items: [{
        id: variantId,
        quantity: quantity || 1
      }]
    };
    if (properties) {
      body.items[0].properties = properties;
    }

    if (busy) return;
    busy = true;
    try {
      await cartFetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      await refreshCart();
      openDrawer();
    } catch (err) {
      showError(err.message);
    } finally {
      busy = false;
    }
  }

  /* ── Open / Close ────────────────────────────────────────────────── */

  /**
   * Open the cart drawer.
   * @param {HTMLElement} [trigger] - The element that triggered the open (for focus return).
   */
  function openDrawer(trigger) {
    if (trigger) triggerEl = trigger;
    /* Prevent layout shift from scrollbar disappearing */
    var scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarW + 'px';

    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    refreshCart();

    /* Move focus into drawer */
    const closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  /**
   * Close the cart drawer and return focus to trigger.
   */
  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';

    /* Return focus */
    if (triggerEl && typeof triggerEl.focus === 'function') {
      triggerEl.focus({ preventScroll: true });
      triggerEl = null;
    }
  }

  /* ── Focus trap ──────────────────────────────────────────────────── */

  /**
   * Trap focus within the cart drawer while it's open.
   * @param {KeyboardEvent} e
   */
  function handleFocusTrap(e) {
    if (e.key !== 'Tab') return;
    if (drawer.getAttribute('aria-hidden') === 'true') return;

    const focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Event listeners ─────────────────────────────────────────────── */

  /* Close buttons */
  closeBtns.forEach((btn) => {
    btn.addEventListener('click', closeDrawer);
  });

  /* Overlay click */
  overlay.addEventListener('click', closeDrawer);

  /* Escape key */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer();
    }
  });

  /* Focus trap */
  document.addEventListener('keydown', handleFocusTrap);

  /* Delegated clicks on quantity +/- and remove */
  itemsList.addEventListener('click', (e) => {
    const minusBtn = e.target.closest('[data-qty-minus]');
    const plusBtn  = e.target.closest('[data-qty-plus]');

    if (minusBtn) {
      const key = minusBtn.dataset.key;
      const item = minusBtn.closest('[data-cart-item-key]');
      const qtyEl = item.querySelector('.cart-drawer__qty-value');
      const currentQty = parseInt(qtyEl.textContent, 10);
      updateItem(key, Math.max(0, currentQty - 1));
    }

    if (plusBtn) {
      const key = plusBtn.dataset.key;
      const item = plusBtn.closest('[data-cart-item-key]');
      const qtyEl = item.querySelector('.cart-drawer__qty-value');
      const currentQty = parseInt(qtyEl.textContent, 10);
      updateItem(key, currentQty + 1);
    }
  });

  /* Listen for the custom cart:open event (dispatched by add-to-cart buttons) */
  document.addEventListener('cart:open', (e) => {
    const trigger = e.detail && e.detail.trigger ? e.detail.trigger : null;
    openDrawer(trigger);
  });

  /* Listen for cart:add event (from product forms) */
  document.addEventListener('cart:add', async (e) => {
    if (!e.detail || !e.detail.variantId) return;
    await addItem(e.detail.variantId, e.detail.quantity || 1, e.detail.properties);
  });

  /* ── Public API ──────────────────────────────────────────────────── */
  window.NoaraCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart,
    add: addItem,
    update: updateItem,
    remove: removeItem
  };
})();
